import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { experiments as expApi } from '../api/client';
import { buildPublicReportPath } from '../utils/publicReports';

const C = {
  void: '#020508',
  deep: '#060b14',
  surface: '#0a1120',
  raised: '#0d1626',
  b0: '#0f1a2e',
  b1: '#1e293b',
  b2: '#334155',
  t1: '#e2e8f0',
  t2: '#b6c3d8',
  t3: '#8396b3',
  t4: '#627795',
  cyan: '#22d3ee',
  violet: '#a78bfa',
  green: '#4ade80',
  gold: '#fbbf24',
  orange: '#fb923c',
  pink: '#f472b6',
  red: '#f87171',
  mono: "'JetBrains Mono',monospace",
  display: "'Syne',sans-serif",
};

const MODULE_META = {
  auto_eda: { label: 'AUTO EDA', icon: '◉', color: C.cyan },
  ml_experiments: { label: 'ML EXPERIMENTS', icon: '⚡', color: C.orange },
  dl_experiments: { label: 'DL EXPERIMENTS', icon: '🧠', color: C.green },
  doc_analysis: { label: 'DOC ANALYSIS', icon: '⬡', color: C.violet },
  image_insights: { label: 'IMAGE INSIGHTS', icon: '◈', color: C.pink },
  audio_video: { label: 'AUDIO / VIDEO', icon: '◎', color: C.gold },
  speech_studio: { label: 'SPEECH STUDIO', icon: '◎', color: C.gold },
  video_intelligence: { label: 'VIDEO INTEL', icon: '▣', color: C.orange },
};

const FILTER_ORDER = [
  'all',
  'auto_eda',
  'ml_experiments',
  'dl_experiments',
  'doc_analysis',
  'image_insights',
  'audio_video',
  'speech_studio',
  'video_intelligence',
];

const SORTS = [
  { id: 'recent', label: 'RECENT' },
  { id: 'views', label: 'MOST VIEWED' },
  { id: 'upvotes', label: 'TOP VOTED' },
  { id: 'comments', label: 'MOST DISCUSSED' },
];

function colorFromText(text) {
  const palette = [C.cyan, C.violet, C.green, C.gold, C.orange, C.pink];
  const source = String(text || 'jorvex');
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) & 0xffff;
  }
  return palette[Math.abs(hash) % palette.length];
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function compactNumber(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  if (numeric >= 1000000) return `${(numeric / 1000000).toFixed(numeric >= 10000000 ? 0 : 1)}M`;
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(numeric >= 10000 ? 0 : 1)}K`;
  return `${numeric}`;
}

function normalizeExp(raw) {
  const summary = raw.feed_summary || {};
  const dataset = summary.dataset || {};
  const social = raw.social || {};
  const filteredMetricChips = (Array.isArray(summary.metric_chips) ? summary.metric_chips : [])
    .filter((metric) => !['ROWS', 'COLS', 'QUALITY'].includes(String(metric?.label || '').toUpperCase()));
  return {
    id: raw.id,
    slug: raw.slug || '',
    title: raw.title || 'Untitled Experiment',
    description: raw.description || '',
    author: raw.username ? `@${raw.username}` : (raw.display_name || 'anonymous'),
    authorColor: colorFromText(raw.username || raw.display_name || raw.id),
    type: raw.type || 'auto_eda',
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    views: Number(raw.view_count || 0),
    reportPath: raw.report_path || buildPublicReportPath(raw.id, raw.slug || raw.title || ''),
    publicUrl: raw.public_url || '',
    createdAt: raw.created_at || raw.updated_at || '',
    time: relativeTime(raw.created_at || raw.updated_at),
    dataset: {
      name: dataset.name || '',
      rows: Number(dataset.rows || 0),
      cols: Number(dataset.cols || 0),
      target: dataset.target || '',
      task: dataset.task || '',
    },
    metricChips: filteredMetricChips,
    insights: Array.isArray(summary.insights) ? summary.insights : [],
    tabsCompleted: Array.isArray(summary.tabs_completed) ? summary.tabs_completed : [],
    qualityScore: typeof summary.quality_score === 'number' ? summary.quality_score : null,
    reportReady: !!summary.report_ready,
    social: {
      upvotes: Number(social.upvotes || 0),
      stars: Number(social.stars || 0),
      comments_count: Number(social.comments_count || 0),
      viewer: {
        has_upvoted: !!social.viewer?.has_upvoted,
        has_starred: !!social.viewer?.has_starred,
      },
      recent_comments: Array.isArray(social.recent_comments) ? social.recent_comments : [],
      comments: Array.isArray(social.comments) ? social.comments : null,
    },
  };
}

function mergeSocial(exp, social) {
  if (!social) return exp;
  return {
    ...exp,
    social: {
      upvotes: Number(social.upvotes || 0),
      stars: Number(social.stars || 0),
      comments_count: Number(social.comments_count || 0),
      viewer: {
        has_upvoted: !!social.viewer?.has_upvoted,
        has_starred: !!social.viewer?.has_starred,
      },
      recent_comments: Array.isArray(social.recent_comments) ? social.recent_comments : (exp.social?.recent_comments || []),
      comments: Array.isArray(social.comments) ? social.comments : (exp.social?.comments || null),
    },
  };
}

function Chip({ label, color, active = false, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 999,
        border: `1px solid ${active ? color : `${color}22`}`,
        background: active ? `${color}18` : `${color}0d`,
        color: active ? color : C.t3,
        fontFamily: C.mono,
        fontSize: 8,
        fontWeight: 800,
        letterSpacing: '.1em',
        cursor: clickable ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function MetricTile({ label, value, color }) {
  return (
    <div style={{
      minWidth: 86,
      padding: '8px 10px',
      borderRadius: 4,
      border: `1px solid ${C.b1}`,
      background: C.raised,
    }}>
      <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: C.display, fontSize: 15, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

function SocialButton({ icon, count, color, active, onClick, disabled = false, clickable = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '0px',
        borderRadius: 0,
        border: 'none',
        background: 'transparent',
        color: active ? color : C.t2,
        cursor: !clickable ? 'default' : (disabled ? 'wait' : 'pointer'),
        fontFamily: C.mono,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '.08em',
      }}
    >
      <span style={{ color: active ? color : C.t3 }}>{icon}</span>
      <span style={{ color: active ? color : C.t4 }}>{compactNumber(count)}</span>
    </button>
  );
}

function CommentItem({ comment }) {
  const authorColor = colorFromText(comment.username || comment.display_name || comment.id);
  return (
    <div style={{ padding: '10px 12px', borderRadius: 6, background: C.deep, border: `1px solid ${C.b0}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: C.mono, fontSize: 8, color: authorColor, fontWeight: 800 }}>
          {comment.username ? `@${comment.username}` : (comment.display_name || 'researcher')}
        </span>
        <span style={{ fontFamily: C.mono, fontSize: 8, color: C.t4 }}>{relativeTime(comment.created_at)}</span>
      </div>
      <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.7 }}>{comment.body}</div>
    </div>
  );
}

function ExperimentCard({
  exp,
  commentsOpen,
  commentDraft,
  commentBusy,
  commentsLoading,
  reactionBusy,
  isAuthenticated,
  onOpenComments,
  onCommentDraftChange,
  onAddComment,
  onToggleUpvote,
  onToggleStar,
  onAuthOpen,
}) {
  const meta = MODULE_META[exp.type] || { label: exp.type, icon: '◈', color: C.t3 };
  const comments = Array.isArray(exp.social.comments) ? exp.social.comments : exp.social.recent_comments;
  return (
    <article style={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 10,
      border: `1px solid ${C.b1}`,
      background: 'linear-gradient(180deg, rgba(10,17,32,0.98) 0%, rgba(2,5,8,0.98) 100%)',
      boxShadow: '0 14px 40px rgba(0,0,0,0.28)',
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: `radial-gradient(circle at top right, ${meta.color}20, transparent 36%)`,
      }} />

      <div style={{ position: 'relative', padding: '18px 18px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${exp.authorColor}44`,
            background: `${exp.authorColor}14`,
            color: exp.authorColor,
            fontFamily: C.display,
            fontWeight: 900,
            fontSize: 12,
          }}>
            {String(exp.author || '?').replace('@', '').charAt(0).toUpperCase() || '?'}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: exp.authorColor, fontWeight: 800 }}>{exp.author}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 3 }}>
              <span style={{ fontFamily: C.mono, fontSize: 8, color: C.t4 }}>{exp.time}</span>
              <span style={{ fontFamily: C.mono, fontSize: 8, color: C.t4 }}>{compactNumber(exp.views)} views</span>
            </div>
          </div>

          <Chip label={`${meta.icon} ${meta.label}`} color={meta.color} />
        </div>

        <div style={{ fontFamily: C.display, fontWeight: 900, fontSize: 21, color: C.t1, lineHeight: 1.12, marginBottom: 8 }}>
          {exp.title}
        </div>

        {exp.description && (
          <div style={{ fontFamily: C.mono, fontSize: 10, color: C.t3, lineHeight: 1.75, marginBottom: 12 }}>
            {exp.description}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
          <div style={{ padding: '9px 10px', borderRadius: 6, background: C.raised, border: `1px solid ${C.b1}` }}>
            <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 4 }}>DATASET</div>
            <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, lineHeight: 1.45 }}>{exp.dataset.name || 'Published dataset'}</div>
          </div>
          <div style={{ padding: '9px 10px', borderRadius: 6, background: C.raised, border: `1px solid ${C.b1}` }}>
            <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 4 }}>SHAPE</div>
            <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, lineHeight: 1.45 }}>
              {exp.dataset.rows > 0 ? `${compactNumber(exp.dataset.rows)} rows` : 'Rows hidden'}
              {exp.dataset.cols > 0 ? ` · ${exp.dataset.cols} cols` : ''}
            </div>
          </div>
          <div style={{ padding: '9px 10px', borderRadius: 6, background: C.raised, border: `1px solid ${C.b1}` }}>
            <div style={{ fontFamily: C.mono, fontSize: 7, color: C.t4, letterSpacing: '.12em', marginBottom: 4 }}>TARGET / TASK</div>
            <div style={{ fontSize: 11, color: C.t1, fontWeight: 700, lineHeight: 1.45 }}>
              {exp.dataset.target || 'No explicit target'}
              {exp.dataset.task ? ` · ${String(exp.dataset.task).toUpperCase()}` : ''}
            </div>
          </div>
        </div>

        {exp.metricChips.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {exp.metricChips.slice(0, 4).map((metric) => (
              <MetricTile key={`${exp.id}_${metric.label}`} label={metric.label} value={metric.value} color={meta.color} />
            ))}
          </div>
        )}

        {exp.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {exp.tags.map((tag) => (
              <span key={`${exp.id}_${tag}`} style={{
                padding: '4px 8px',
                borderRadius: 999,
                background: C.b0,
                border: `1px solid ${C.b1}`,
                color: C.t3,
                fontFamily: C.mono,
                fontSize: 8,
              }}>
                #{tag}
              </span>
            ))}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 12,
          borderTop: `1px solid ${C.b0}`,
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SocialButton
              icon={exp.social.viewer.has_upvoted ? '♥' : '♡'}
              count={exp.social.upvotes}
              color={C.green}
              active={exp.social.viewer.has_upvoted}
              disabled={reactionBusy === `${exp.id}:upvote`}
              onClick={() => {
                if (!isAuthenticated) {
                  onAuthOpen?.('signup');
                  return;
                }
                onToggleUpvote(exp.id);
              }}
            />
            <SocialButton
              icon="⑂"
              count={exp.social.stars}
              color={C.gold}
              active={exp.social.viewer.has_starred}
              disabled={reactionBusy === `${exp.id}:star`}
              onClick={() => {
                if (!isAuthenticated) {
                  onAuthOpen?.('signup');
                  return;
                }
                onToggleStar(exp.id);
              }}
            />
            <SocialButton
              icon="💬"
              count={exp.social.comments_count}
              color={C.cyan}
              active={commentsOpen}
              disabled={commentsLoading}
              onClick={() => onOpenComments(exp.id)}
            />
            <SocialButton
              icon="👁"
              count={exp.views}
              color={C.t3}
              active={false}
              onClick={undefined}
              clickable={false}
            />
          </div>

          <a
            href={exp.reportPath}
            style={{
              padding: '0px',
              border: 'none',
              background: 'transparent',
              color: C.cyan,
              cursor: 'pointer',
              fontFamily: C.mono,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '.12em',
              textDecoration: 'none',
            }}
          >
            VIEW REPORT
          </a>
        </div>

        {commentsOpen && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.b0}`, paddingTop: 14 }}>
            <div style={{ fontFamily: C.mono, fontSize: 8, color: C.t4, letterSpacing: '.14em', marginBottom: 10 }}>
              COMMUNITY DISCUSSION
            </div>

            {isAuthenticated ? (
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                <textarea
                  value={commentDraft}
                  onChange={(event) => onCommentDraftChange(exp.id, event.target.value)}
                  placeholder="Add a useful comment, critique, or follow-up finding"
                  rows={3}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    borderRadius: 6,
                    background: C.raised,
                    border: `1px solid ${C.b1}`,
                    color: C.t1,
                    padding: '10px 12px',
                    fontFamily: C.mono,
                    fontSize: 10,
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: C.mono, fontSize: 8, color: C.t4 }}>{commentDraft.length} / 800</div>
                  <button
                    type="button"
                    onClick={() => onAddComment(exp.id)}
                    disabled={commentBusy || !commentDraft.trim()}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: 'none',
                      background: C.cyan,
                      color: C.void,
                      cursor: commentBusy ? 'wait' : 'pointer',
                      fontFamily: C.mono,
                      fontSize: 8,
                      fontWeight: 800,
                      letterSpacing: '.12em',
                    }}
                  >
                    {commentBusy ? 'POSTING...' : 'POST COMMENT'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: 'wrap',
                padding: '10px 12px',
                marginBottom: 12,
                borderRadius: 6,
                background: C.raised,
                border: `1px solid ${C.b1}`,
              }}>
                <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.6 }}>Sign in to upvote, star, and join the discussion.</div>
                <button
                  type="button"
                  onClick={() => onAuthOpen?.('signup')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: `1px solid ${C.cyan}44`,
                    background: `${C.cyan}12`,
                    color: C.cyan,
                    cursor: 'pointer',
                    fontFamily: C.mono,
                    fontSize: 8,
                    fontWeight: 800,
                    letterSpacing: '.12em',
                  }}
                >
                  JOIN TO COMMENT
                </button>
              </div>
            )}

            {commentsLoading ? (
              <div style={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>Loading comments...</div>
            ) : comments.length > 0 ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {comments.map((comment) => <CommentItem key={comment.id} comment={comment} />)}
              </div>
            ) : (
              <div style={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>No comments yet.</div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default function PublicExperiments({ isAuthenticated, onNavigate, onAuthOpen, preloadedFeed = null, preloadedFeedError = '' }) {
  const [experiments, setExperiments] = useState(() => Array.isArray(preloadedFeed) ? preloadedFeed.map(normalizeExp) : []);
  const [loading, setLoading] = useState(() => !Array.isArray(preloadedFeed) && !preloadedFeedError);
  const [fetchError, setFetchError] = useState(preloadedFeedError || '');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [search, setSearch] = useState('');
  const [reportError, setReportError] = useState('');
  const [reactionBusy, setReactionBusy] = useState('');
  const [commentsOpenId, setCommentsOpenId] = useState('');
  const [commentsLoadingId, setCommentsLoadingId] = useState('');
  const [commentBusyId, setCommentBusyId] = useState('');
  const [commentDrafts, setCommentDrafts] = useState({});
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    if (Array.isArray(preloadedFeed)) {
      setExperiments(preloadedFeed.map(normalizeExp));
      setFetchError(preloadedFeedError || '');
      setLoading(false);
      return;
    }

    if (preloadedFeedError) {
      setFetchError(preloadedFeedError);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFetchError('');

    expApi.publicFeed({ limit: 50 })
      .then((data) => {
        if (cancelled) return;
        setExperiments((data.experiments || []).map(normalizeExp));
      })
      .catch((error) => {
        if (cancelled) return;
        setFetchError(error?.data?.error || error?.message || 'Failed to load public experiments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [preloadedFeed, preloadedFeedError]);

  const stats = useMemo(() => {
    return experiments.reduce((acc, exp) => {
      acc.views += exp.views;
      acc.upvotes += exp.social.upvotes;
      acc.comments += exp.social.comments_count;
      if (exp.reportReady) acc.reportReady += 1;
      return acc;
    }, { views: 0, upvotes: 0, comments: 0, reportReady: 0 });
  }, [experiments]);

  const moduleCounts = useMemo(() => {
    const counts = new Map();
    experiments.forEach((exp) => {
      counts.set(exp.type, (counts.get(exp.type) || 0) + 1);
    });
    return counts;
  }, [experiments]);

  const filtered = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    let next = [...experiments];

    if (filter !== 'all') {
      next = next.filter((exp) => exp.type === filter);
    }

    if (query) {
      next = next.filter((exp) => {
        const haystack = [
          exp.title,
          exp.description,
          exp.author,
          exp.dataset.name,
          exp.dataset.target,
          exp.dataset.task,
          ...exp.tags,
          ...exp.insights,
          ...exp.tabsCompleted,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    }

    if (sort === 'views') {
      next.sort((left, right) => right.views - left.views);
    } else if (sort === 'upvotes') {
      next.sort((left, right) => right.social.upvotes - left.social.upvotes);
    } else if (sort === 'comments') {
      next.sort((left, right) => right.social.comments_count - left.social.comments_count);
    } else {
      next.sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
    }

    return next;
  }, [deferredSearch, experiments, filter, sort]);

  const patchExperiment = (experimentId, updater) => {
    setExperiments((current) => current.map((exp) => (exp.id === experimentId ? updater(exp) : exp)));
  };

  const handleToggleReaction = async (experimentId, type) => {
    setReportError('');
    setReactionBusy(`${experimentId}:${type}`);
    try {
      const data = type === 'upvote' ? await expApi.upvote(experimentId) : await expApi.star(experimentId);
      patchExperiment(experimentId, (exp) => mergeSocial(exp, data.social));
    } catch (error) {
      setReportError(error?.data?.error || error?.message || 'Failed to update social reaction');
    } finally {
      setReactionBusy('');
    }
  };

  const loadComments = async (experimentId) => {
    setCommentsLoadingId(experimentId);
    try {
      const data = await expApi.comments(experimentId);
      patchExperiment(experimentId, (exp) => mergeSocial(exp, data.social));
    } catch (error) {
      setReportError(error?.data?.error || error?.message || 'Failed to load comments');
    } finally {
      setCommentsLoadingId('');
    }
  };

  const handleOpenComments = async (experimentId) => {
    const nextOpen = commentsOpenId === experimentId ? '' : experimentId;
    setCommentsOpenId(nextOpen);
    if (!nextOpen) return;
    const target = experiments.find((exp) => exp.id === experimentId);
    if (!target?.social.comments) {
      await loadComments(experimentId);
    }
  };

  const handleAddComment = async (experimentId) => {
    const body = String(commentDrafts[experimentId] || '').trim();
    if (!body) return;
    setCommentBusyId(experimentId);
    try {
      const data = await expApi.addComment(experimentId, body);
      patchExperiment(experimentId, (exp) => mergeSocial(exp, data.social));
      setCommentDrafts((current) => ({ ...current, [experimentId]: '' }));
    } catch (error) {
      setReportError(error?.data?.error || error?.message || 'Failed to post comment');
    } finally {
      setCommentBusyId('');
    }
  };

  return (
    <div style={{ maxWidth: 1380, margin: '0 auto', padding: '0 0 56px' }}>
      <style>{`
        .jorvex-public-cards {
          display: grid;
          gap: 14px;
        }
        .jorvex-public-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .jorvex-public-toolbar {
          display: grid;
          gap: 12px;
        }
        @media (max-width: 900px) {
          .jorvex-public-topbar {
            align-items: stretch;
          }
        }
      `}</style>

      <br/>

      <div style={{ padding: '0 24px' }}>
        {reportError && (
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${C.red}33`,
            background: `${C.red}12`,
            color: C.red,
            fontFamily: C.mono,
            fontSize: 9,
          }}>
            {reportError}
          </div>
        )}

        <div className="jorvex-public-toolbar" style={{
          borderRadius: 10,
          border: `1px solid ${C.b1}`,
          background: 'linear-gradient(180deg, rgba(10,17,32,0.98) 0%, rgba(2,5,8,0.98) 100%)',
          padding: '14px',
          marginBottom: 14,
        }}>
          <div className="jorvex-public-topbar">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FILTER_ORDER.map((id) => {
                if (id !== 'all' && !moduleCounts.has(id)) return null;
                const meta = id === 'all' ? { label: 'ALL', color: C.t3 } : MODULE_META[id];
                const count = id === 'all' ? experiments.length : (moduleCounts.get(id) || 0);
                return (
                  <Chip
                    key={id}
                    label={`${meta.label} ${count}`}
                    color={meta.color}
                    active={filter === id}
                    onClick={() => setFilter(id)}
                  />
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="search title, dataset, tag, finding, author"
                style={{
                  minWidth: 260,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  background: C.raised,
                  border: `1px solid ${C.b1}`,
                  borderRadius: 999,
                  color: C.t1,
                  padding: '10px 12px',
                  fontFamily: C.mono,
                  fontSize: 9,
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {SORTS.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.label}
                    color={sort === item.id ? C.cyan : C.t3}
                    active={sort === item.id}
                    onClick={() => setSort(item.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div style={{ fontFamily: C.mono, fontSize: 8, color: C.t4, letterSpacing: '.12em' }}>
            {loading ? 'LOADING FEED' : `${filtered.length} RESULTS`}
          </div>
        </div>

        {loading ? (
          <div className="jorvex-public-cards">
            {[0, 1, 2].map((index) => (
              <div key={index} style={{
                height: 280,
                borderRadius: 10,
                border: `1px solid ${C.b1}`,
                background: 'linear-gradient(180deg, rgba(10,17,32,0.98) 0%, rgba(2,5,8,0.98) 100%)',
                opacity: 0.42,
              }} />
            ))}
          </div>
        ) : fetchError ? (
          <div style={{
            padding: '42px 24px',
            borderRadius: 10,
            border: `1px solid ${C.b1}`,
            background: 'linear-gradient(180deg, rgba(10,17,32,0.98) 0%, rgba(2,5,8,0.98) 100%)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 34, opacity: 0.25, marginBottom: 10 }}>⚠</div>
            <div style={{ fontFamily: C.display, fontWeight: 800, fontSize: 16, color: C.t2, marginBottom: 6 }}>Could not load the public feed</div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>{fetchError}</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: '42px 24px',
            borderRadius: 10,
            border: `1px solid ${C.b1}`,
            background: 'linear-gradient(180deg, rgba(10,17,32,0.98) 0%, rgba(2,5,8,0.98) 100%)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 34, opacity: 0.25, marginBottom: 10 }}>⬡</div>
            <div style={{ fontFamily: C.display, fontWeight: 800, fontSize: 16, color: C.t2, marginBottom: 6 }}>No experiments matched this view</div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>Try another module filter or search term.</div>
          </div>
        ) : (
          <div className="jorvex-public-cards">
            {filtered.map((exp) => (
              <ExperimentCard
                key={exp.id}
                exp={exp}
                commentsOpen={commentsOpenId === exp.id}
                commentDraft={commentDrafts[exp.id] || ''}
                commentBusy={commentBusyId === exp.id}
                commentsLoading={commentsLoadingId === exp.id}
                reactionBusy={reactionBusy}
                isAuthenticated={isAuthenticated}
                onOpenComments={handleOpenComments}
                onCommentDraftChange={(experimentId, value) => setCommentDrafts((current) => ({ ...current, [experimentId]: value.slice(0, 800) }))}
                onAddComment={handleAddComment}
                onToggleUpvote={(id) => handleToggleReaction(id, 'upvote')}
                onToggleStar={(id) => handleToggleReaction(id, 'star')}
                onAuthOpen={onAuthOpen}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}